import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Copy, Download, Robot, VolumeUp } from 'react-bootstrap-icons';

import { downloadBlobAsFile, downloadHtmlStringAsTxtFile } from '@/lib/utils';
import {
	aiConversationService,
	documentService,
	GetDocumentAudioResponseBody,
	GetDocumentSummaryResponseBody,
} from '@/lib/services';
import { useAnalytics, useCopyTextToClipboard, useHandleError, useNavigationConfig } from '@/hooks';
import { AiSummaryLanguageModal, ConfirmDialog, Loader } from '@/components';
import { AiChatWindow } from '@/components/ai-chat-window';
import { AiConversation } from '@/lib/models';
import { ErrorConfig } from '@/lib/http-client';

interface ResearchDetailAiSummaryProps {
	documentId: string;
	documentTitle: string;
	allowAiIndexing: boolean;
}

const AI_SUMMARY_UNAVAILABLE_MESSAGE =
	'AI Generated summary is not available for this document as it is based on third-party materials.';
const DOCUMENT_ASK_HACKETT_UNAVAILABLE_MESSAGE =
	'AskHackett is not available for this document as it is based on third-party materials.';

export const ResearchDetailAiSummary = ({
	documentId,
	documentTitle,
	allowAiIndexing,
}: ResearchDetailAiSummaryProps) => {
	const handleError = useHandleError();
	const { copyHtmlToClipboard } = useCopyTextToClipboard();
	const { navigationConfig } = useNavigationConfig();
	const analytics = useAnalytics();

	const [showTranslateModal, setShowTranslateModal] = useState(false);
	const [showAudioModal, setShowAudioModal] = useState(false);
	const [showAudioConfirmModal, setShowAudioConfirmModal] = useState(false);

	const [summary, setSummary] = useState('');
	const [summaryIsProcessing, setSummaryIsProcessing] = useState(false);
	const summaryPollingTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
	const summaryRequest = useRef<() => Promise<GetDocumentSummaryResponseBody>>();

	const [audioSummaries, setAudioSummaries] = useState<GetDocumentAudioResponseBody[]>([]);
	const [currentAudio, setCurrentAudio] = useState<GetDocumentAudioResponseBody | undefined>(undefined);
	const [activeAudioLanguageCode, setActiveAudioLanguageCode] = useState<string | undefined>(undefined);
	const audioPollingTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
	const audioRequest = useRef<() => Promise<GetDocumentAudioResponseBody>>();

	const [inAiConversation, setInAiConversation] = useState(false);
	const [documentConversation, setDocumentConversation] = useState<AiConversation | undefined>(undefined);
	const isSummaryAvailable =
		allowAiIndexing &&
		!summaryIsProcessing &&
		summary !== 'Unable to generate summary.' &&
		summary.replace(/<[^>]*>/g, '').trim().length > 0;

	const pollForAudio = useCallback(async () => {
		try {
			if (!audioRequest.current) {
				throw new Error('audioRequest is undefined.');
			}

			const response = await audioRequest.current();
			setCurrentAudio(response);
			setAudioSummaries((as) => as.filter((as) => as.languageCode !== response.languageCode));

			if (response.processing) {
				audioPollingTimeout.current = setTimeout(pollForAudio, 2000);
			} else {
				if (audioPollingTimeout.current) {
					clearTimeout(audioPollingTimeout.current);
					audioPollingTimeout.current = undefined;
				}

				setCurrentAudio(undefined);
				setAudioSummaries((as) => {
					const priorAudioSummaryExists = !!as.find((as) => as.languageCode === response.languageCode);
					return priorAudioSummaryExists
						? as.map((as) => (as.languageCode === response.languageCode ? response : as))
						: [response, ...as];
				});
			}
		} catch (err) {
			handleError(err);
			setCurrentAudio(undefined);
		}
	}, [handleError]);

	const pollForSummary = useCallback(async () => {
		if (!allowAiIndexing) {
			setSummary(AI_SUMMARY_UNAVAILABLE_MESSAGE);
			setSummaryIsProcessing(false);
			setAudioSummaries([]);
			setCurrentAudio(undefined);
			return;
		}

		setSummaryIsProcessing(true);

		try {
			if (!summaryRequest.current) {
				summaryRequest.current = documentService.getDocumentSummary(documentId, {
					language: 'en',
				}).fetch;
			}

			const response = await summaryRequest.current();
			const currentlyProcessingAudio = response.audioSummaries.find((a) => a.processing);
			const nonProcessingAudio = response.audioSummaries.filter((a) => !a.processing);

			setSummaryIsProcessing(response.processing);
			setAudioSummaries(nonProcessingAudio);
			if (currentlyProcessingAudio) {
				audioRequest.current = documentService.getDocumentAudio(documentId, {
					language: currentlyProcessingAudio.languageCode,
				}).fetch;
				pollForAudio();
			}

			if (response.processing) {
				summaryPollingTimeout.current = setTimeout(pollForSummary, 2000);
				return;
			} else {
				if (summaryPollingTimeout.current) {
					clearTimeout(summaryPollingTimeout.current);
					summaryPollingTimeout.current = undefined;
				}

				setSummary(response.summary ?? '');
			}
		} catch (error) {
			setSummary('Unable to generate summary.');
			handleError(error);
			setSummaryIsProcessing(false);
		}
	}, [allowAiIndexing, documentId, handleError, pollForAudio]);

	useEffect(() => {
		pollForSummary();

		return () => {
			if (summaryPollingTimeout.current) {
				clearInterval(summaryPollingTimeout.current);
				summaryPollingTimeout.current = undefined;
			}
		};
	}, [pollForSummary]);

	useEffect(() => {
		return () => {
			if (audioPollingTimeout.current) {
				clearInterval(audioPollingTimeout.current);
				audioPollingTimeout.current = undefined;
			}
		};
	}, []);

	useEffect(() => {
		if (!inAiConversation || !allowAiIndexing) {
			setDocumentConversation(undefined);
			return;
		}

		let isActive = true;

		const fetchDocumentConversation = async () => {
			try {
				const response = await aiConversationService.getDocumentConversation(documentId).fetch();
				if (isActive) {
					setDocumentConversation(response.aiConversation);
				}
			} catch (error) {
				if ((error as ErrorConfig).status === 404) {
					if (isActive) {
						setDocumentConversation(undefined);
					}
					return;
				}

				handleError(error);
			}
		};

		void fetchDocumentConversation();

		return () => {
			isActive = false;
		};
	}, [allowAiIndexing, documentId, handleError, inAiConversation]);

	const handlePlayAudioButtonClick = (audio: GetDocumentAudioResponseBody) => {
		try {
			if (!audio.audioUrl) {
				throw new Error('audio.audioUrl is undefined.');
			}

			setActiveAudioLanguageCode((currentLanguageCode) =>
				currentLanguageCode === audio.languageCode ? undefined : audio.languageCode
			);
		} catch (error) {
			handleError(error);
		}
	};

	const handleDownloadAudioButtonClick = async (audio: GetDocumentAudioResponseBody) => {
		try {
			if (!audio.audioUrl) {
				throw new Error('audio.audioUrl is undefined.');
			}

			const audioBlob = await documentService.downloadDocumentAudio(documentId, audio.languageCode).fetch();
			downloadBlobAsFile(`${documentTitle}-ai-audio-summary-${audio.language}.mp3`, audioBlob);
		} catch (error) {
			handleError(error);
		}
	};

	const handleDownloadSummaryButtonClick = async () => {
		downloadHtmlStringAsTxtFile(`${documentTitle}-ai-summary`, summary);
	};

	return (
		<>
			<AiSummaryLanguageModal
				title="Translate Summary"
				inputLabel="Language"
				submitButtonTitle="Translate"
				show={showTranslateModal}
				onHide={() => {
					setShowTranslateModal(false);
				}}
				onSubmit={(language) => {
					setShowTranslateModal(false);
					analytics.trackEvent('translate_summary');
					summaryRequest.current = documentService.getDocumentSummary(documentId, { language }).fetch;
					pollForSummary();
				}}
			/>

			<AiSummaryLanguageModal
				title="Create Audio Summary"
				inputLabel="Select language for audio"
				submitButtonTitle="Create Audio"
				show={showAudioModal}
				onHide={() => {
					setShowAudioModal(false);
				}}
				onSubmit={(language) => {
					setShowAudioModal(false);
					setShowAudioConfirmModal(true);
					analytics.trackEvent('create_audio_summary');

					audioRequest.current = documentService.getDocumentAudio(documentId, { language }).fetch;
					pollForAudio();
				}}
			/>

			<ConfirmDialog
				show={showAudioConfirmModal}
				onHide={() => {
					setShowAudioConfirmModal(false);
				}}
				onConfirm={() => {
					setShowAudioConfirmModal(false);
				}}
				title="Create Audio Summary"
				confirmText="Ok"
			>
				<p className="mb-0">Your audio summary is being created. Please check back soon.</p>
			</ConfirmDialog>

			<Row>
				<Col xl={inAiConversation ? 6 : 8}>
					<div className="mb-8 border">
						<div className="bg-gray-200 border-bottom px-6 py-2 d-flex align-items-center justify-content-between">
							<h5 className="mb-0 font-weight-semi-bold">AI Summary</h5>
							<div className="d-flex align-items-center">
								<Button
									className="mr-3 font-weight-bold text-primary"
									variant="link"
									onClick={() => {
										setShowTranslateModal(true);
									}}
									disabled={!allowAiIndexing || summaryIsProcessing}
								>
									Translate Summary
								</Button>
								<Button
									className="p-2 text-primary"
									variant="link"
									disabled={!allowAiIndexing || summaryIsProcessing}
									onClick={() => {
										copyHtmlToClipboard(summary);
									}}
								>
									<Copy width={20} height={20} />
								</Button>
								<Button
									className="p-2 text-primary"
									variant="link"
									disabled={!allowAiIndexing || summaryIsProcessing}
									onClick={handleDownloadSummaryButtonClick}
								>
									<Download width={20} height={20} />
								</Button>
							</div>
						</div>
						<div className="bg-white p-8">
							{summaryIsProcessing ? <Loader /> : <div dangerouslySetInnerHTML={{ __html: summary }} />}
						</div>
					</div>
				</Col>
				<Col xl={inAiConversation ? 6 : 4}>
					{inAiConversation ? (
						<div className="border">
							<AiChatWindow
								isMicroChat
								initialConversation={documentConversation}
								noConversationTemplate={
									<h3 className="text-primary">
										How can AskHackett&trade; help you with this document?
									</h3>
								}
								textareaPlaceholder="Message AskHackett™"
								onCloseButtonClick={() => {
									setInAiConversation(false);
								}}
								queryContext={{
									documentId,
								}}
								unavailableMessage={
									allowAiIndexing ? undefined : DOCUMENT_ASK_HACKETT_UNAVAILABLE_MESSAGE
								}
							/>
						</div>
					) : (
						<>
							{navigationConfig?.allowHackettAi && (
								<Button
									className="mb-3 d-flex align-items-center w-100 justify-content-center"
									variant="primary"
									disabled={allowAiIndexing && !isSummaryAvailable}
									onClick={() => {
										setInAiConversation(true);
									}}
								>
									<Robot className="mr-2" width={20} height={20} />
									AskHackett&trade;
								</Button>
							)}
							<Button
								className="d-block w-100"
								variant="outline-primary"
								onClick={() => {
									setShowAudioModal(true);
								}}
								disabled={!allowAiIndexing || currentAudio?.processing}
							>
								Create AI Audio Summary
							</Button>
							{(currentAudio || audioSummaries.length > 0) && (
								<>
									<hr className="mt-8 mb-7" />
									<h4 className="mb-6">AI Audio Summaries</h4>
									{currentAudio?.processing && (
										<div className="mb-2 bg-white border p-4 d-flex align-items-center justify-content-between flex-wrap">
											<h5 className="mb-0 font-weight-bold">
												AI Audio Summary in {currentAudio.language}
											</h5>
											<p className="mb-0 text-muted small">In progress</p>
										</div>
									)}
									{audioSummaries.map((audioSummary) => (
										<div key={audioSummary.languageCode} className="mb-2 bg-white border p-4">
											{audioSummary.processing ? (
												<div className="d-flex align-items-center justify-content-between flex-wrap">
													<h5 className="mb-0 font-weight-bold">
														AI Audio Summary in {audioSummary.language}
													</h5>
													<p className="mb-0 text-muted small">In progress</p>
												</div>
											) : (
												<>
													<div className="d-flex align-items-center justify-content-between flex-wrap">
														<div>
															<h5 className="mb-0 font-weight-bold">
																AI Audio Summary in {audioSummary.language}
															</h5>
															<p className="mb-0 text-muted small">
																Created {audioSummary.createdDescription}
															</p>
														</div>
														<div className="d-flex align-items-center">
															<Button
																aria-label={`Listen to AI Audio Summary in ${audioSummary.language}`}
																className="p-2 text-primary"
																title="Listen"
																variant="link"
																onClick={() => {
																	handlePlayAudioButtonClick(audioSummary);
																}}
															>
																<VolumeUp width={20} height={20} />
															</Button>
															<Button
																aria-label={`Download AI Audio Summary in ${audioSummary.language}`}
																className="p-2 text-primary"
																title="Download"
																variant="link"
																onClick={() => {
																	handleDownloadAudioButtonClick(audioSummary);
																}}
															>
																<Download width={20} height={20} />
															</Button>
														</div>
													</div>
													{activeAudioLanguageCode === audioSummary.languageCode && (
														<audio
															className="mt-3 w-100"
															controls
															preload="metadata"
															src={audioSummary.audioUrl ?? undefined}
														/>
													)}
												</>
											)}
										</div>
									))}
								</>
							)}
						</>
					)}
				</Col>
			</Row>
		</>
	);
};
